import { create } from 'zustand';
import { toast } from 'sonner';
import {
  listFeedback,
  submitFeedback,
  deleteFeedback,
  type FeedbackRow,
  type FeedbackRating,
} from '../lib/api';

/**
 * Admin AI-generation feedback store. Holds the loaded user's feedback
 * rows keyed by ISO workout_date for fast day-card lookups.
 *
 * Designed for the day-level scope (one rating per admin per user per date).
 * Season / playground scopes can be added later — the underlying table
 * supports them, but the UI surface in v1 only writes scope='day'.
 *
 * Why a separate store from workoutStore: keeps the feedback fetch off
 * the season-load critical path. If listing feedback 500s the workout
 * editor still works; we just don't show prior ratings.
 */

interface FeedbackState {
  loadedUserId: string | null;
  isLoading: boolean;
  /** Day-scope rows keyed by workout_date. */
  byDate: Record<string, FeedbackRow>;
  /** Week-scope rows keyed by the week's start ISO date. */
  byWeekStart: Record<string, FeedbackRow>;
  /** In-flight submit state keyed by `${scope}:${date}` so day and week
   *  controls on the same screen track independently. */
  pendingByKey: Record<string, boolean>;

  /** Fetch all feedback rows for the given user. Idempotent — calling
   *  with the same userId after a successful load is a no-op. */
  loadForUser: (userId: string) => Promise<void>;

  /** Submit a day-scoped rating + notes. Updates byDate on success. */
  submitDay: (args: {
    userId: string;
    workoutDate: string;
    rating: FeedbackRating;
    notes?: string;
  }) => Promise<FeedbackRow | null>;

  /** Submit a week-scoped rating + notes. Anchored on the week's start
   *  ISO date. Updates byWeekStart on success. */
  submitWeek: (args: {
    userId: string;
    weekStart: string;
    rating: FeedbackRating;
    notes?: string;
  }) => Promise<FeedbackRow | null>;

  /** Remove a feedback row by id and from both maps. */
  remove: (feedbackId: string) => Promise<void>;

  /** Wipe state — called when switching users. */
  reset: () => void;
}

/** Composite pending key — keep day vs week pending state independent
 *  so a week-scope save doesn't disable day-scope controls (or vice versa). */
const pendingKey = (scope: 'day' | 'week', date: string) => `${scope}:${date}`;

export const useFeedbackStore = create<FeedbackState>((set, get) => ({
  loadedUserId: null,
  isLoading: false,
  byDate: {},
  byWeekStart: {},
  pendingByKey: {},

  loadForUser: async (userId: string) => {
    const { loadedUserId, isLoading } = get();
    if (loadedUserId === userId || isLoading) return;

    set({
      isLoading: true,
      loadedUserId: userId,
      byDate: {},
      byWeekStart: {},
    });
    try {
      const resp = await listFeedback(userId);
      const byDate: Record<string, FeedbackRow> = {};
      const byWeekStart: Record<string, FeedbackRow> = {};
      for (const row of resp.rows) {
        // Backend orders newest first, so the first row for each (scope,
        // date) populates and later iterations skip — effectively
        // "most-recent wins" within each map.
        if (row.scope === 'day' && row.workout_date) {
          if (!byDate[row.workout_date]) byDate[row.workout_date] = row;
        } else if (row.scope === 'week' && row.workout_date) {
          if (!byWeekStart[row.workout_date]) byWeekStart[row.workout_date] = row;
        }
      }
      set({ byDate, byWeekStart, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load feedback';
      // Silent toast — feedback is non-critical, keep the workout editor usable.
      toast.error(message);
      set({ isLoading: false });
    }
  },

  submitDay: async ({ userId, workoutDate, rating, notes }) => {
    const key = pendingKey('day', workoutDate);
    set((s) => ({ pendingByKey: { ...s.pendingByKey, [key]: true } }));
    try {
      const resp = await submitFeedback({
        user_id: userId,
        scope: 'day',
        workout_date: workoutDate,
        rating,
        notes,
      });
      set((s) => ({
        byDate: { ...s.byDate, [workoutDate]: resp.feedback },
      }));
      toast.success(resp.created ? 'Feedback saved' : 'Feedback updated');
      return resp.feedback;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save feedback';
      toast.error(message);
      return null;
    } finally {
      set((s) => {
        const next = { ...s.pendingByKey };
        delete next[key];
        return { pendingByKey: next };
      });
    }
  },

  submitWeek: async ({ userId, weekStart, rating, notes }) => {
    const key = pendingKey('week', weekStart);
    set((s) => ({ pendingByKey: { ...s.pendingByKey, [key]: true } }));
    try {
      const resp = await submitFeedback({
        user_id: userId,
        scope: 'week',
        workout_date: weekStart,
        rating,
        notes,
      });
      set((s) => ({
        byWeekStart: { ...s.byWeekStart, [weekStart]: resp.feedback },
      }));
      toast.success(resp.created ? 'Weekly feedback saved' : 'Weekly feedback updated');
      return resp.feedback;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save feedback';
      toast.error(message);
      return null;
    } finally {
      set((s) => {
        const next = { ...s.pendingByKey };
        delete next[key];
        return { pendingByKey: next };
      });
    }
  },

  remove: async (feedbackId: string) => {
    try {
      await deleteFeedback(feedbackId);
      set((s) => {
        const nextDay = { ...s.byDate };
        const nextWeek = { ...s.byWeekStart };
        for (const k of Object.keys(nextDay)) {
          if (nextDay[k].id === feedbackId) delete nextDay[k];
        }
        for (const k of Object.keys(nextWeek)) {
          if (nextWeek[k].id === feedbackId) delete nextWeek[k];
        }
        return { byDate: nextDay, byWeekStart: nextWeek };
      });
      toast.success('Feedback removed');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove feedback';
      toast.error(message);
    }
  },

  reset: () => set({ loadedUserId: null, isLoading: false, byDate: {}, byWeekStart: {}, pendingByKey: {} }),
}));
